import { addMonths, isAfter, parseISO, startOfDay } from 'date-fns';
import { calculateAgeFromDateOfBirth } from './dateOfBirth';

export type FamilySchoolLookupCandidate = {
  urn: string;
  name: string;
  postcode: string | null;
  town: string | null;
};

export type FamilySchoolChildLink = {
  id?: string;
  name: string;
  schoolName?: string | null;
};

export type FamilySchoolCalendarEventLike = {
  id: string;
  title: string;
  date: string;
  notes?: string | null;
  sourceLabel?: string | null;
  source_label?: string | null;
};

export type FamilySchoolCalendarImportLike = {
  authorityName?: string | null;
  authority_name?: string | null;
  sourceLabel?: string | null;
  source_label?: string | null;
};

export type LinkedSchoolGroup = {
  schoolName: string;
  childNames: string[];
};

export type SchoolCalendarGroupWithEvents<T extends FamilySchoolCalendarEventLike> = LinkedSchoolGroup & {
  events: T[];
};

const UK_POSTCODE_PATTERN = /\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/i;

export function normalizeUkPostcode(value: string | null | undefined) {
  const trimmed = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
  if (trimmed.length < 5) {
    return null;
  }

  return `${trimmed.slice(0, -3)} ${trimmed.slice(-3)}`;
}

export function extractUkPostcode(value: string | null | undefined) {
  const match = String(value || '').toUpperCase().match(UK_POSTCODE_PATTERN);
  return match ? normalizeUkPostcode(match[1]) : null;
}

function normalizeSchoolName(value: string | null | undefined) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function dedupeNames(values: string[]) {
  const seen = new Set<string>();
  const result: string[] = [];

  values.forEach((value) => {
    const trimmed = value.trim().replace(/\s+/g, ' ');
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) return;
    seen.add(key);
    result.push(trimmed);
  });

  return result;
}

function formatNameList(values: string[]) {
  if (values.length <= 1) {
    return values[0] || '';
  }

  if (values.length === 2) {
    return `${values[0]} and ${values[1]}`;
  }

  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
}

export function resolveSchoolLookupPostcode({
  postcode,
  address,
}: {
  postcode?: string | null;
  address?: string | null;
}) {
  return normalizeUkPostcode(postcode) || extractUkPostcode(address) || '';
}

export function isChildSchoolSelectionRelevant({
  dateOfBirth,
  age,
}: {
  dateOfBirth?: string | null;
  age?: number | null;
}) {
  const resolvedAge = calculateAgeFromDateOfBirth(dateOfBirth || null) ?? age ?? null;
  return resolvedAge !== null && resolvedAge < 18;
}

export function collectSelectedSchoolNames(
  children: Array<{ schoolName?: string | null }>,
) {
  return Array.from(
    new Set(
      children
        .map((child) => String(child.schoolName || '').trim())
        .filter(Boolean),
      ),
  );
}

export function buildLinkedSchoolGroups({
  children,
  selectedSchoolNames,
}: {
  children: FamilySchoolChildLink[];
  selectedSchoolNames?: string[] | null;
}) {
  const savedSchoolNames = collectSelectedSchoolNames(children);
  const schoolNames = dedupeNames(selectedSchoolNames?.length ? selectedSchoolNames : savedSchoolNames);
  const groups: LinkedSchoolGroup[] = schoolNames.map((schoolName) => {
    const matchingChildren = children
      .filter((child) => normalizeSchoolName(child.schoolName) === normalizeSchoolName(schoolName))
      .map((child) => child.name.trim())
      .filter(Boolean);

    return {
      schoolName,
      childNames: dedupeNames(matchingChildren),
    };
  });

  return groups.filter((group) => group.schoolName);
}

export function buildSchoolCalendarIdentitySummary({
  children,
  selectedSchoolNames,
}: {
  children: FamilySchoolChildLink[];
  selectedSchoolNames?: string[] | null;
}) {
  const groups = buildLinkedSchoolGroups({ children, selectedSchoolNames });
  const linkedChildNames = dedupeNames(groups.flatMap((group) => group.childNames));
  const schoolNames = dedupeNames(groups.map((group) => group.schoolName));
  const savedSchoolNames = dedupeNames(collectSelectedSchoolNames(children));
  const importSchoolNames = dedupeNames(selectedSchoolNames || []);
  const savedSchoolKeys = new Set(savedSchoolNames.map((value) => value.toLowerCase()));
  const importSchoolKeys = new Set(importSchoolNames.map((value) => value.toLowerCase()));
  const importNeedsRefresh = savedSchoolNames.length > 0 && (
    savedSchoolNames.length !== importSchoolNames.length
    || savedSchoolNames.some((value) => !importSchoolKeys.has(value.toLowerCase()))
    || importSchoolNames.some((value) => !savedSchoolKeys.has(value.toLowerCase()))
  );

  return {
    groups,
    linkedChildNames,
    schoolNames,
    childSummary: formatNameList(linkedChildNames),
    schoolSummary: formatNameList(schoolNames),
    importNeedsRefresh,
  };
}

export function isSchoolWebsiteImport(importRecord?: FamilySchoolCalendarImportLike | null) {
  const authorityName = normalizeSchoolName(importRecord?.authorityName || importRecord?.authority_name || '');
  const sourceLabel = normalizeSchoolName(importRecord?.sourceLabel || importRecord?.source_label || '');

  return authorityName === 'selected school websites' || sourceLabel === 'school website import';
}

export function buildSchoolCalendarEventPresentation({
  event,
  children,
  selectedSchoolNames,
}: {
  event: FamilySchoolCalendarEventLike;
  children: FamilySchoolChildLink[];
  selectedSchoolNames?: string[] | null;
}) {
  const identity = buildSchoolCalendarIdentitySummary({ children, selectedSchoolNames });
  const eventSourceLabel = event.sourceLabel || event.source_label || null;
  const matchedGroup = eventSourceLabel
    ? identity.groups.find((group) => normalizeSchoolName(group.schoolName) === normalizeSchoolName(eventSourceLabel))
    : null;
  const primarySchool = matchedGroup?.schoolName || (identity.groups.length === 1 ? identity.groups[0].schoolName : null);
  const linkedChildrenTitle = matchedGroup?.childNames.length
    ? formatNameList(matchedGroup.childNames)
    : null;
  const singleChild = matchedGroup?.childNames.length === 1 ? matchedGroup.childNames[0] : null;
  const isSchoolSpecific = Boolean(matchedGroup);
  const genericSelectedSchoolSummary = identity.schoolSummary || null;

  const title = linkedChildrenTitle
    ? `${linkedChildrenTitle}: ${event.title}`
    : isSchoolSpecific && primarySchool
      ? `${primarySchool}: ${event.title}`
      : `Shared school dates: ${event.title}`;

  const subtitleParts: string[] = [];

  if (isSchoolSpecific && singleChild && primarySchool) {
    subtitleParts.push(primarySchool);
  }

  if (isSchoolSpecific && linkedChildrenTitle && identity.groups.length === 1 && primarySchool) {
    subtitleParts.push(primarySchool);
  } else if (isSchoolSpecific && !linkedChildrenTitle && identity.groups.length === 1 && primarySchool) {
    subtitleParts.push(primarySchool);
  } else if (isSchoolSpecific && identity.groups.length > 1 && identity.schoolSummary) {
    subtitleParts.push(identity.schoolSummary);
  } else if (!isSchoolSpecific && genericSelectedSchoolSummary) {
    subtitleParts.push(`Selected schools: ${genericSelectedSchoolSummary}`);
  }

  if (event.notes) {
    subtitleParts.push(event.notes);
  } else if (!isSchoolSpecific) {
    subtitleParts.push('Shared council-published date - confirm with each school');
  } else {
    subtitleParts.push('Imported from the school website');
  }

  return {
    title,
    subtitle: subtitleParts.join(' · '),
  };
}

export function groupUpcomingSchoolCalendarEventsBySchool<T extends FamilySchoolCalendarEventLike>({
  events,
  children,
  selectedSchoolNames,
  from = new Date(),
  monthsAhead = 3,
}: {
  events: T[];
  children: FamilySchoolChildLink[];
  selectedSchoolNames?: string[] | null;
  from?: Date;
  monthsAhead?: number;
}) {
  const groups = buildLinkedSchoolGroups({ children, selectedSchoolNames });
  const upcomingEvents = getUpcomingSchoolCalendarEvents(events, { from, monthsAhead });

  return groups.map((group) => ({
    ...group,
    events: upcomingEvents.filter((event) => normalizeSchoolName(event.sourceLabel || event.source_label) === normalizeSchoolName(group.schoolName)),
  })) satisfies SchoolCalendarGroupWithEvents<T>[];
}

export function getUpcomingSchoolCalendarEvents<T extends FamilySchoolCalendarEventLike>(
  events: T[],
  {
    from = new Date(),
    monthsAhead = 3,
  }: {
    from?: Date;
    monthsAhead?: number;
  } = {},
) {
  const windowStart = startOfDay(from);
  const windowEnd = startOfDay(addMonths(windowStart, monthsAhead));

  return events
    .filter((event) => {
      const eventDate = startOfDay(parseISO(event.date));
      return !isAfter(windowStart, eventDate) && !isAfter(eventDate, windowEnd);
    })
    .sort((left, right) => left.date.localeCompare(right.date));
}
